import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, Coins, Search } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, EQUIPMENT, formatPrice, summarizeItem } from "@/lib/equipment";
import IllegalWarningModal from "@/components/IllegalWarningModal";

export default function Shop({ open, onClose, character, onPurchase }) {
  const [category, setCategory] = useState("Weapons");
  const [q, setQ] = useState("");
  const [pendingIllegal, setPendingIllegal] = useState(null);

  const items = useMemo(() => {
    return EQUIPMENT.filter((it) => it.category === category)
      .filter((it) => !q || it.name.toLowerCase().includes(q.toLowerCase()));
  }, [category, q]);

  const completePurchase = (it) => {
    const cost = it.price || 0;
    const sats = character?.sats || 0;
    const target = it.target || "gear";
    const currentList = Array.isArray(character[target]) ? character[target] : [];
    const label = it.damage
      ? `${it.name} (${it.damage})`
      : it.tier
      ? `${it.name} [Tier ${it.tier}] ${it.note ? "— " + it.note : ""}`.trim()
      : it.note
      ? `${it.name} — ${it.note}`
      : it.name;
    onPurchase({
      [target]: [...currentList, { id: crypto.randomUUID(), text: label, price: cost, damage: it.damage, tier: it.tier, category: it.category, illegal: !!it.illegal, ...(it.props || {}) }],
      sats: sats - cost,
    });
    toast.success(`ACQUIRED · ${it.name}`, { description: `-${formatPrice(cost)} · Added to ${target.toUpperCase()}` });
  };

  const buy = (it) => {
    const cost = it.price || 0;
    const sats = character?.sats || 0;
    if (cost > sats) {
      toast.error(`INSUFFICIENT FUNDS`, { description: `${it.name} costs ${formatPrice(cost)} — you have ${formatPrice(sats)}` });
      return;
    }
    const target = it.target || "gear";
    if (target === "gear") {
      const currentGear = character.gear || [];
      const isBackpackPurchase = /backpack/i.test(it.name);
      const wouldHaveBackpack = isBackpackPurchase || currentGear.some((g) => /backpack/i.test(g.text || ""));
      const newCap = 4 + (wouldHaveBackpack ? 6 : 0);
      if (currentGear.length + 1 > newCap) {
        toast.error("STORAGE FULL", { description: `Carry limit ${currentGear.length}/${newCap}. Buy a Backpack first.` });
        return;
      }
    }
    if (it.illegal) {
      setPendingIllegal(it);
      return;
    }
    completePurchase(it);
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-yellow w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            data-testid="shop-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="flex items-center gap-3">
                <ShoppingCart className="text-[color:var(--accent-yellow)]" />
                <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tighter">BLACK//MARKET</div>
                <span className="tape tape-cyan text-[10px] hidden sm:inline">CY_BORG CORE</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 border-2 border-[color:var(--accent-yellow)] px-3 py-1">
                  <Coins size={14} className="text-[color:var(--accent-yellow)]" />
                  <span data-testid="shop-sats" className="font-display font-black text-xl text-[color:var(--accent-yellow)] tabular-nums">
                    {formatPrice(character?.sats || 0)}
                  </span>
                </div>
                <button data-testid="shop-close-btn" onClick={onClose} aria-label="Close" className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Filter bar */}
            <div className="flex flex-col sm:flex-row gap-2 px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)]">
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`shop-cat-${c.toLowerCase()}`}
                    onClick={() => setCategory(c)}
                    className={`px-3 py-2 border-2 text-xs font-bold uppercase tracking-widest ${category === c ? "bg-[color:var(--accent-yellow)] text-[color:var(--bg-void)] border-[color:var(--accent-yellow)]" : "border-[color:var(--border-hard)] text-[color:var(--text-secondary)] hover:border-[color:var(--accent-yellow)]"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="flex-1 flex items-center gap-2 border-2 border-[color:var(--border-hard)] px-3">
                <Search size={14} className="text-[color:var(--text-muted)]" />
                <input
                  data-testid="shop-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search…"
                  className="cy-input border-0 flex-1 px-0"
                />
              </div>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shop-items">
              {items.map((it) => {
                const canAfford = (character?.sats || 0) >= it.price;
                return (
                  <div key={it.id} className={`panel p-3 flex flex-col gap-2 ${canAfford ? "" : "opacity-60"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display font-black uppercase tracking-tight text-sm sm:text-base truncate flex items-center gap-1">
                          {it.name}
                          {it.illegal && (
                            <span
                              data-testid={`illegal-badge-${it.id}`}
                              title="ILLEGAL — buying, carrying or using this may attract heat"
                              className="inline-flex items-center gap-0.5 bg-[color:var(--accent-magenta)] text-[color:var(--bg-void)] text-[8px] font-bold uppercase tracking-widest px-1 py-0.5"
                            >
                              ⚠ ILLEGAL
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-0.5">{summarizeItem(it)}</div>
                      </div>
                      <div className="tape text-[10px] shrink-0">{formatPrice(it.price)}</div>
                    </div>
                    <button
                      data-testid={`buy-${it.id}`}
                      onClick={() => buy(it)}
                      disabled={!canAfford}
                      className={`cy-btn text-xs mt-auto ${canAfford ? "cy-btn-solid-yellow" : ""}`}
                    >
                      {canAfford ? "BUY" : "TOO POOR"}
                    </button>
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="col-span-full font-mono text-sm text-[color:var(--text-muted)] p-6">NO ITEMS MATCH.</div>
              )}
            </div>
          </motion.div>
        </div>
      )}
      <IllegalWarningModal
        open={!!pendingIllegal}
        item={pendingIllegal}
        onCancel={() => setPendingIllegal(null)}
        onConfirm={() => { const it = pendingIllegal; setPendingIllegal(null); completePurchase(it); }}
      />
    </AnimatePresence>
  );
}
